// Configurações › Minicursos: lista de cursos e editor do curso escolhido (?curso=<id>&aba=...).
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Modal from 'react-modal';
import { doc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { FiAlertTriangle } from 'react-icons/fi';
import { firestore } from '../config/firebaseConfig';
import Carregando from './Carregando';
import Dialogo from './minicursos/Dialogo';
import EditorCurso from './minicursos/EditorCurso';
import ListaCursos from './minicursos/ListaCursos';
import NovoCursoModal from './minicursos/NovoCursoModal';
import { useDadosMinicursos } from './minicursos/useDadosMinicursos';
import { avisosDoCurso, cursoAtivo, plural } from './minicursos/util';
import './MinicursoManager.css'; // original: também traz regras usadas por outras telas
import './minicursos/EditorMinicursos.css';

Modal.setAppElement('#root');

const MinicursoManager = () => {
  const tenantId = useMemo(() => localStorage.getItem('tenantId') || '', []);
  const dados = useDadosMinicursos(tenantId);
  const { carregando, erro, cursos, etapasPorCurso, andamentoPorCurso } = dados;
  const [params, setParams] = useSearchParams();
  const [criando, setCriando] = useState(false);
  const [mudancaStatus, setMudancaStatus] = useState(null); // { curso, ativar, problemas }
  const [salvandoStatus, setSalvandoStatus] = useState(false);
  const sujoRef = useRef(false);

  const cursoId = params.get('curso');
  const aba = params.get('aba') || 'etapas';
  const curso = cursos.find((c) => c.id === cursoId) || null;

  const podeSair = () => !sujoRef.current
    || window.confirm('Há alterações não salvas neste curso. Sair e descartar?');

  const abrir = (id) => setParams({ curso: id });
  const voltar = () => { if (podeSair()) setParams({}); };
  const trocarAba = useCallback((novaAba) => setParams({ curso: cursoId, aba: novaAba }, { replace: true }), [cursoId, setParams]);

  const gravarStatus = async (alvo, ativar) => {
    setSalvandoStatus(true);
    try {
      await updateDoc(doc(firestore, 'minicursos', alvo.id), { status: ativar ? 'ativo' : 'inativo' });
      toast.success(ativar
        ? `"${alvo.nome}" ativado: já aparece no menu Cursos do WhatsApp.`
        : `"${alvo.nome}" desativado. Nada foi apagado.`);
      setMudancaStatus(null);
    } catch (e) {
      console.error('Erro ao mudar o status do minicurso:', e);
      toast.error('Não foi possível mudar o status do curso.');
    } finally {
      setSalvandoStatus(false);
    }
  };

  // etapas: as do editor (iguais às que o bot lê) ou, na lista, as do tenant
  const alternarStatus = (alvo, etapas) => {
    const lista = etapas || etapasPorCurso[alvo.id] || [];
    if (cursoAtivo(alvo)) {
      setMudancaStatus({ curso: alvo, ativar: false });
      return;
    }
    if (!lista.length) {
      toast.error('Adicione pelo menos uma etapa antes de ativar o curso.');
      return;
    }
    const problemas = avisosDoCurso(lista, { ...alvo, status: 'ativo' }).filter((a) => a.nivel === 'perigo');
    if (problemas.length) setMudancaStatus({ curso: alvo, ativar: true, problemas });
    else gravarStatus(alvo, true);
  };

  if (!tenantId) return <div className="mc-pagina"><p className="qz-empty">Empresa não identificada. Saia e entre de novo.</p></div>;
  if (erro) return <div className="mc-pagina"><p className="qz-empty">{erro}</p></div>;
  if (carregando) return <div className="mc-pagina"><Carregando texto="Carregando minicursos..." /></div>;

  const emAndamento = mudancaStatus ? (andamentoPorCurso[mudancaStatus.curso.id] || []).length : 0;

  return (
    <div className="minicurso-manager">
      {cursoId && !curso ? (
        <div className="mc-pagina">
          <div className="qz-empty mc-vazio">
            <p>Este curso não existe mais ou é de outra empresa.</p>
            <button type="button" className="btn-secondary mc-botao" onClick={() => setParams({})}>Ver todos os cursos</button>
          </div>
        </div>
      ) : curso ? (
        <EditorCurso
          key={curso.id}
          curso={curso}
          cursos={cursos}
          tenantId={tenantId}
          dados={dados}
          andamento={andamentoPorCurso[curso.id] || []}
          aba={aba}
          onAba={trocarAba}
          onVoltar={voltar}
          onAlternarStatus={alternarStatus}
          sujoRef={sujoRef}
        />
      ) : (
        <ListaCursos
          cursos={cursos}
          etapasPorCurso={etapasPorCurso}
          andamentoPorCurso={andamentoPorCurso}
          onAbrir={abrir}
          onNovo={() => setCriando(true)}
          onAlternarStatus={(alvo) => alternarStatus(alvo, null)}
        />
      )}

      {criando && (
        <NovoCursoModal
          tenantId={tenantId}
          cursos={cursos}
          temas={dados.temas}
          onFechar={() => setCriando(false)}
          onCriado={(id) => { setCriando(false); setParams({ curso: id, aba: 'etapas' }); }}
        />
      )}

      {mudancaStatus && !mudancaStatus.ativar && (
        <Dialogo
          titulo="Desativar o curso?"
          confirmar="Desativar curso"
          perigo
          ocupado={salvandoStatus}
          onConfirmar={() => gravarStatus(mudancaStatus.curso, false)}
          onFechar={() => setMudancaStatus(null)}
        >
          <p><strong>{mudancaStatus.curso.nome}</strong> sai do menu Cursos do WhatsApp e o bot para de enviar novas etapas.</p>
          {emAndamento > 0 && (
            <p className="mc-alerta mc-alerta--atencao">
              <FiAlertTriangle aria-hidden="true" />
              <span>{plural(emAndamento, 'aluno em andamento fica pausado', 'alunos em andamento ficam pausados')} até você reativar o curso.</span>
            </p>
          )}
          <p className="mc-suave">Nada é apagado: etapas, progresso e notas continuam guardados. Dá para reativar a qualquer momento.</p>
        </Dialogo>
      )}

      {mudancaStatus && mudancaStatus.ativar && (
        <Dialogo
          titulo="Ativar com problemas?"
          confirmar="Ativar mesmo assim"
          ocupado={salvandoStatus}
          onConfirmar={() => gravarStatus(mudancaStatus.curso, true)}
          onFechar={() => setMudancaStatus(null)}
        >
          <p>Ao ativar, <strong>{mudancaStatus.curso.nome}</strong> aparece no menu Cursos do WhatsApp. Antes, veja:</p>
          <ul className="mc-lista-simples">
            {mudancaStatus.problemas.map((p) => <li key={p.id}>{p.texto}</li>)}
          </ul>
        </Dialogo>
      )}
    </div>
  );
};

export default MinicursoManager;
